import { Status, useInsightPanelDataFetcher } from "./useInsightPanelDataFetcher"
import {
  InsightsPanel,
  InsightsPanelContent,
  InsightsPanelErrorDefault,
  InsightsPanelHeader,
  InsightsPanelNoDataDefault,
} from "~/src/components/Dashboard/InsightsPanel"
import { Country } from "~/src/lib/country"
import { getFlagEmoji } from "~/src/lib/emoji"
import { Format } from "~/src/lib/format"
import { WithErrors } from "~/src/types/withErrors"

type ShippingDestinationsDataRow = {
  countryCode: string
  orderCount: number
  percentageOfTotal: number
}

export type ShippingDestinationsData = WithErrors<{ destinations: ShippingDestinationsDataRow[] }>

const NUMBER_OF_DESTINATIONS_SHOWN = 8
const NUMBER_OF_DESTINATIONS_SHOWN_MOBILE = 3

export function StorefrontInsightsShippingDestinationsPanel() {
  const { isLoading, data } = useInsightPanelDataFetcher<ShippingDestinationsData>("shipping_destinations")

  const destinations = data?.destinations

  let status: Status
  if (isLoading) {
    status = "loading"
  } else if (data?.errors) {
    status = "error"
  } else if (destinations && destinations.length > 0) {
    status = "success"
  } else {
    status = "noData"
  }

  return <StorefrontInsightsShippingDestinationsRenderPanel status={status} destinations={destinations} />
}

export function StorefrontInsightsShippingDestinationsRenderPanel({
  status,
  destinations,
}: {
  status: Status
  destinations?: ShippingDestinationsDataRow[]
}) {
  let countryDisplayNames: Record<string, string> | undefined

  if (destinations) {
    countryDisplayNames = destinations.reduce(
      (acc, destination) => {
        const country = Country.fromAlpha2(destination.countryCode)
        acc[destination.countryCode] =
          `${getFlagEmoji(destination.countryCode)} ${country?.name ?? destination.countryCode}`
        return acc
      },
      {} as Record<string, string>
    )
  }

  return (
    <InsightsPanel className="@container h-full w-full">
      <InsightsPanelHeader title="Shipping destinations" />
      <InsightsPanelContent className="overflow-y-auto">
        <ShippingDestinationsContent status={status} rows={destinations} countryDisplayNames={countryDisplayNames} />
      </InsightsPanelContent>
    </InsightsPanel>
  )
}

function ShippingDestinationsContent({
  status,
  rows,
  countryDisplayNames,
}: {
  status: Status
  rows: ShippingDestinationsDataRow[] | undefined
  countryDisplayNames?: Record<string, string>
}) {
  if (status === "loading") return <ShippingDestinationsSkeleton />
  if (status === "error") return <InsightsPanelErrorDefault />
  if (status === "noData" || !rows) return <InsightsPanelNoDataDefault />

  return <ShippingDestinationsDisplay rows={rows} countryDisplayNames={countryDisplayNames} />
}

function ShippingDestinationsDisplay({
  rows,
  countryDisplayNames,
}: {
  rows: ShippingDestinationsDataRow[]
  countryDisplayNames?: Record<string, string>
}) {
  const getDisplayName = (destination: ShippingDestinationsDataRow) => {
    return (
      countryDisplayNames?.[destination.countryCode] ||
      `${getFlagEmoji(destination.countryCode)} ${Country.fromAlpha2(destination.countryCode)?.name ?? destination.countryCode}`
    )
  }

  const rowsToShow = rows.length < NUMBER_OF_DESTINATIONS_SHOWN ? rows : rows.slice(0, NUMBER_OF_DESTINATIONS_SHOWN)

  return (
    <div className="h-full">
      <table className="w-full [&_td]:py-4 [&_th]:py-2">
        <tbody>
          {rowsToShow.map((destination, index) => (
            <tr
              key={destination.countryCode}
              className={`border-b py-4 last:border-b-0 ${
                index >= NUMBER_OF_DESTINATIONS_SHOWN_MOBILE ? "hidden @sm:table-row" : ""
              }`}
            >
              <td className="flex items-center gap-2 pl-4">{getDisplayName(destination)}</td>
              <td className="pr-4 text-right">
                {Format.number(destination.orderCount)} {destination.orderCount === 1 ? "Order" : "Orders"} (
                {destination.percentageOfTotal.toFixed(2)}%)
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ShippingDestinationsSkeleton() {
  return (
    <div className="h-full">
      {/* Mobile skeleton */}
      <div className="@sm:hidden">
        {[...Array(NUMBER_OF_DESTINATIONS_SHOWN_MOBILE)].map((_, idx) => (
          <div key={idx} className="flex animate-pulse gap-4 border-b p-4">
            <div className="flex w-full justify-between gap-2">
              <div className="h-3 w-44 rounded-full bg-zinc-200" />
              <div className="h-3 w-16 rounded-full bg-zinc-200" />
            </div>
          </div>
        ))}
      </div>

      {/* Desktop skeleton */}
      <div className="hidden @sm:block">
        <table className="w-full [&_td]:py-4 [&_th]:py-2">
          <tbody>
            {[...Array(NUMBER_OF_DESTINATIONS_SHOWN)].map((_, idx) => (
              <tr key={idx} className="animate-pulse border-b last:border-b-0">
                <td className="flex items-center pl-4">
                  <div className="h-3 w-48 rounded-full bg-zinc-200" />
                </td>
                <td className="pr-4 text-right">
                  <div className="ml-auto h-3 w-20 rounded-full bg-zinc-200" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
